
import * as THREE from "../../libs/three.js/build/three.module.js";
import { TextSprite } from "../TextSprite.js";
import { Utils } from "../utils.js";
import { Line2 } from "../../libs/three.js/examples/jsm/lines/Line2.js";
import { LineGeometry } from "../../libs/three.js/examples/jsm/lines/LineGeometry.js";
import { LineMaterial } from "../../libs/three.js/examples/jsm/lines/LineMaterial.js";
import { ShowMode } from "../defines.js";

export class Measure extends THREE.Object3D {
	constructor() {
		super();

		this.constructor.counter = (this.constructor.counter === undefined) ? 0 : this.constructor.counter + 1;

		this.name = 'Measure_' + this.constructor.counter;
		this.points = [];
		this._showDistances = true;
		this._showCoordinates = false;
		this._showArea = false;
		this._closed = true;
		this._showAngles = false;
		this._showCircle = false;
		this._showHeight = false;
		this._showEdges = true;
		this._showAzimuth = false;
		this.maxMarkers = Number.MAX_SAFE_INTEGER;
		this._isTemperature = false;
		this._depthMode = false;

		this.sphereGeometry = new THREE.SphereGeometry(0.4, 10, 10);
		this.color = new THREE.Color(0xff0000);

		this.spheres = [];
		this.edges = [];
		this.sphereLabels = [];
		this.edgeLabels = [];
		this.angleLabels = [];
		this.coordinateLabels = [];

		this.heightEdge = this.createHeightLine();
		this.heightLabel = this.createHeightLabel();
		this.areaLabel = this.createAreaLabel();
		this.circleRadiusLabel = this.createCircleRadiusLabel();
		this.circleRadiusLine = this.createCircleRadiusLine();
		this.circleLine = this.createCircleLine();
		this.circleCenter = this.createCircleCenter();

		this.azimuth = this.createAzimuth();

		this.add(this.heightEdge);
		this.add(this.heightLabel);
		this.add(this.areaLabel);
		this.add(this.circleRadiusLabel);
		this.add(this.circleRadiusLine);
		this.add(this.circleLine);
		this.add(this.circleCenter);

		this.add(this.azimuth.node);

		// 预先绑定所有方法
		this._boundMarkerDrag = this.markerDrag.bind(this);
		this._boundMarkerDrop = this.markerDrop.bind(this);
		this._boundMouseover = this.markerMouseover.bind(this);
		this._boundMouseleave = this.markerMouseleave.bind(this);
	}

	createHeightLine() {
		let lineGeometry = new LineGeometry();

		lineGeometry.setPositions([
			0, 0, 0,
			0, 0, 0,
		]);

		let lineMaterial = new LineMaterial({
			color: 0x00ff00,
			dashSize: 5,
			gapSize: 2,
			linewidth: 2,
			resolution: new THREE.Vector2(1000, 1000),
		});

		lineMaterial.depthTest = this._depthMode;
		const heightEdge = new Line2(lineGeometry, lineMaterial);
		heightEdge.visible = false;

		//this.add(this.heightEdge);

		return heightEdge;
	}

	createHeightLabel() {
		const heightLabel = new TextSprite('');

		heightLabel.setTextColor({ r: 140, g: 250, b: 140, a: 1.0 });
		heightLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
		heightLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 1.0 });
		heightLabel.fontsize = 16;
		heightLabel.material.depthTest = this._depthMode;
		heightLabel.material.opacity = 1;
		heightLabel.visible = false;

		return heightLabel;
	}

	createAreaLabel() {
		const areaLabel = new TextSprite('');

		areaLabel.setTextColor({ r: 140, g: 250, b: 140, a: 1.0 });
		areaLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
		areaLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 1.0 });
		areaLabel.fontsize = 16;
		areaLabel.material.depthTest = this._depthMode;
		areaLabel.material.opacity = 1;
		areaLabel.visible = false;

		return areaLabel;
	}

	createCircleRadiusLabel() {
		const circleRadiusLabel = new TextSprite("");

		circleRadiusLabel.setTextColor({ r: 140, g: 250, b: 140, a: 1.0 });
		circleRadiusLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
		circleRadiusLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 1.0 });
		circleRadiusLabel.fontsize = 16;
		circleRadiusLabel.material.depthTest = this._depthMode;
		circleRadiusLabel.material.opacity = 1;
		circleRadiusLabel.visible = false;

		return circleRadiusLabel;
	}

	createCircleRadiusLine() {
		const lineGeometry = new LineGeometry();

		lineGeometry.setPositions([
			0, 0, 0,
			0, 0, 0,
		]);

		const lineMaterial = new LineMaterial({
			color: 0xff0000,
			linewidth: 2,
			resolution: new THREE.Vector2(1000, 1000),
			gapSize: 1,
			dashed: true,
		});

		lineMaterial.depthTest = this._depthMode;

		const circleRadiusLine = new Line2(lineGeometry, lineMaterial);
		circleRadiusLine.visible = false;

		return circleRadiusLine;
	}

	createCircleLine() {
		const coordinates = [];

		let n = 128;
		for (let i = 0; i <= n; i++) {
			let u0 = 2 * Math.PI * (i / n);
			let u1 = 2 * Math.PI * (i + 1) / n;

			let p0 = new THREE.Vector3(
				Math.cos(u0),
				Math.sin(u0),
				0
			);

			let p1 = new THREE.Vector3(
				Math.cos(u1),
				Math.sin(u1),
				0
			);

			coordinates.push(
				...p0.toArray(),
				...p1.toArray(),
			);
		}

		const geometry = new LineGeometry();
		geometry.setPositions(coordinates);

		const material = new LineMaterial({
			color: 0xff0000,
			dashSize: 5,
			gapSize: 2,
			linewidth: 2,
			resolution: new THREE.Vector2(1000, 1000),
		});

		material.depthTest = this._depthMode;

		const circleLine = new Line2(geometry, material);
		circleLine.visible = false;
		circleLine.computeLineDistances();

		return circleLine;
	}

	createCircleCenter() {
		const sg = new THREE.SphereGeometry(1, 32, 32);
		const sm = new THREE.MeshNormalMaterial();

		const circleCenter = new THREE.Mesh(sg, sm);
		circleCenter.visible = false;

		return circleCenter;
	}

	createLine() {
		const geometry = new LineGeometry();

		geometry.setPositions([
			0, 0, 0,
			0, 0, 0,
		]);

		const material = new LineMaterial({
			color: 0xff0000,
			linewidth: 2,
			resolution: new THREE.Vector2(1000, 1000),
			gapSize: 1,
			dashed: true,
		});

		material.depthTest = this._depthMode;

		const line = new Line2(geometry, material);

		return line;
	}

	createCircle() {

		const coordinates = [];

		let n = 128;
		for (let i = 0; i <= n; i++) {
			let u0 = 2 * Math.PI * (i / n);
			let u1 = 2 * Math.PI * (i + 1) / n;

			let p0 = new THREE.Vector3(
				Math.cos(u0),
				Math.sin(u0),
				0
			);

			let p1 = new THREE.Vector3(
				Math.cos(u1),
				Math.sin(u1),
				0
			);

			coordinates.push(
				...p0.toArray(),
				...p1.toArray(),
			);
		}

		const geometry = new LineGeometry();
		geometry.setPositions(coordinates);

		const material = new LineMaterial({
			color: 0xff0000,
			dashSize: 5,
			gapSize: 2,
			linewidth: 2,
			resolution: new THREE.Vector2(1000, 1000),
		});

		material.depthTest = this._depthMode;

		const line = new Line2(geometry, material);
		line.computeLineDistances();

		return line;

	}

	createAzimuth() {

		const azimuth = {
			label: null,
			center: null,
			target: null,
			north: null,
			centerToNorth: null,
			centerToTarget: null,
			centerToTargetground: null,
			targetgroundToTarget: null,
			circle: null,

			node: null,
		};

		const sg = new THREE.SphereGeometry(1, 32, 32);
		const sm = new THREE.MeshNormalMaterial();

		{
			const label = new TextSprite("");

			label.setTextColor({ r: 140, g: 250, b: 140, a: 1.0 });
			label.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
			label.setBackgroundColor({ r: 0, g: 0, b: 0, a: 1.0 });
			label.fontsize = 16;
			label.material.depthTest = this._depthMode;
			label.material.opacity = 1;

			azimuth.label = label;
		}

		azimuth.center = new THREE.Mesh(sg, sm);
		azimuth.target = new THREE.Mesh(sg, sm);
		azimuth.north = new THREE.Mesh(sg, sm);
		azimuth.centerToNorth = this.createLine();
		azimuth.centerToTarget = this.createLine();
		azimuth.centerToTargetground = this.createLine();
		azimuth.targetgroundToTarget = this.createLine();
		azimuth.circle = this.createCircle();

		azimuth.node = new THREE.Object3D();
		azimuth.node.add(
			azimuth.centerToNorth,
			azimuth.centerToTarget,
			azimuth.centerToTargetground,
			azimuth.targetgroundToTarget,
			azimuth.circle,
			azimuth.label,
			azimuth.center,
			azimuth.target,
			azimuth.north,
		);

		return azimuth;
	}

	createSphereMaterial() {
		let sphereMaterial = new THREE.MeshLambertMaterial({
			color: this.color,
		}
		);

		sphereMaterial.depthTest = this._depthMode;
		sphereMaterial.depthWrite = this._depthMode;

		return sphereMaterial;
	};

	addMarker(point) {
		if (point.x != null) {
			point = { position: point };
		} else if (point instanceof Array) {
			point = { position: new THREE.Vector3(...point) };
		}
		this.points.push(point);

		// sphere
		let sphere = new THREE.Mesh(this.sphereGeometry, this.createSphereMaterial());

		this.add(sphere);
		this.spheres.push(sphere);

		{ // edges
			let lineGeometry = new LineGeometry();
			lineGeometry.setPositions([
				0, 0, 0,
				0, 0, 0,
			]);

			let lineMaterial = new LineMaterial({
				color: 0xff0000,
				linewidth: 2,
				resolution: new THREE.Vector2(1000, 1000),
			});

			lineMaterial.depthTest = this._depthMode;

			let edge = new Line2(lineGeometry, lineMaterial);
			edge.visible = true;

			this.add(edge);
			this.edges.push(edge);
		}

		{ // edge labels
			let edgeLabel = new TextSprite();
			edgeLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
			edgeLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 1.0 });
			edgeLabel.material.depthTest = this._depthMode;
			edgeLabel.visible = false;
			edgeLabel.fontsize = 16;
			this.edgeLabels.push(edgeLabel);
			this.add(edgeLabel);
		}

		{ // angle labels
			let angleLabel = new TextSprite();
			angleLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
			angleLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 1.0 });
			angleLabel.fontsize = 16;
			angleLabel.material.depthTest = this._depthMode;
			angleLabel.material.opacity = 1;
			angleLabel.visible = false;
			this.angleLabels.push(angleLabel);
			this.add(angleLabel);
		}

		{ // coordinate labels
			let coordinateLabel = new TextSprite();
			coordinateLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
			coordinateLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 1.0 });
			coordinateLabel.fontsize = 16;
			coordinateLabel.material.depthTest = this._depthMode;
			coordinateLabel.material.opacity = 1;
			coordinateLabel.visible = false;
			this.coordinateLabels.push(coordinateLabel);
			this.add(coordinateLabel);
		}

		this.markerAddEventListener(sphere)

		let event = {
			type: 'marker_added',
			measurement: this,
			sphere: sphere
		};

		this.dispatchEvent(event);

		this.setMarker(this.points.length - 1, point);
	};

	markerDrag(e) {
		// rAF 节流：GS 场景 WASM raycast 单次 ~几 ms，快速拖拽会阻塞主线程
		// 造成 mousemove 事件合并/丢帧。合并同一帧内多次 drag，只处理最后一次。
		this._pendingDrag = e;
		if (this._rafScheduled) return;
		this._rafScheduled = true;
		requestAnimationFrame(() => {
			this._rafScheduled = false;
			const evt = this._pendingDrag;
			this._pendingDrag = null;
			if (evt) this._processMarkerDrag(evt);
		});
	}

	_processMarkerDrag(e) {
		const camera = e.viewer.scene.getActiveCamera();
		let I = Utils.getMouseIntersection(
			e.drag.end,
			camera,
			e.viewer,
			{ pickClipped: true });

		// Miss 兜底：3DGS 场景在稀薄边缘 / LOD 抽样漏点时 I===null，
		// 用上次命中点建立的观察平面反投影一个虚拟命中，保证 sphere 连贯。
		if (!I && this._lastHitLocation) {
			const world = Utils.mouseToPlaneIntersection(
				e.drag.end, camera, e.viewer.renderer, this._lastHitLocation);
			if (world) {
				I = {
					location: world,
					distance: 0,
					pointcloud: null,
					point: { position: world.clone() }
				};
			}
		}

		if (I) {
			let i = this.spheres.indexOf(e.drag.object);
			if (i !== -1) {
				let point = this.points[i];

				// loop through current keys and cleanup ones that will be orphaned
				for (let key of Object.keys(point)) {
					if (!I.point[key]) {
						delete point[key];
					}
				}

				for (let key of Object.keys(I.point)) {
					if (key !== 'position') {
						point[key] = I.point[key];
					}
				}
				if (this._isTemperature === false)
					this.setPosition(i, I.point.position);
				else {
					this.setPositionAndTemperature(i, I.point.position, I.point.temperature);
				}
			}
			// 记录最近命中，供下一次 miss 兜底
			this._lastHitLocation = I.location.clone();
		}
	}

	markerDrop(e) {
		// 清 rAF/miss-fallback 状态，避免下次拖别的 marker 时用到旧值
		this._pendingDrag = null;
		this._rafScheduled = false;
		this._lastHitLocation = null;

		let i = this.spheres.indexOf(e.drag.object);
		if (i !== -1) {
			this.dispatchEvent({
				'type': 'marker_dropped',
				'measurement': this,
				'index': i
			});
		}
	}

	markerMouseover(e) {
		e.object.material.emissive.setHex(0x888888);
	}

	markerMouseleave(e) {
		e.object.material.emissive.setHex(0x000000);
	}

	markerAddEventListener(sphere) {
		if (!sphere) return;
		if (this._boundMarkerDrag) {
			sphere.addEventListener('drag', this._boundMarkerDrag);
		}
		if (this._boundMarkerDrop) {
			sphere.addEventListener('drop', this._boundMarkerDrop);
		}
		if (this._boundMouseover) {
			sphere.addEventListener('mouseover', this._boundMouseover);
		}
		if (this._boundMouseleave) {
			sphere.addEventListener('mouseleave', this._boundMouseleave);
		}
	}

	markerRemoveEventListener(sphere) {
		if (!sphere) return;
		if (this._boundMarkerDrag) {
			sphere.removeEventListener('drag', this._boundMarkerDrag);
		}
		if (this._boundMarkerDrop) {
			sphere.removeEventListener('drop', this._boundMarkerDrop);
		}
		if (this._boundMouseover) {
			sphere.removeEventListener('mouseover', this._boundMouseover);
		}
		if (this._boundMouseleave) {
			sphere.removeEventListener('mouseleave', this._boundMouseleave);
		}
	}

	removeMarker(index) {
		this.points.splice(index, 1);

		this.remove(this.spheres[index]);

		let edgeIndex = (index === 0) ? 0 : (index - 1);
		this.remove(this.edges[edgeIndex]);
		this.edges.splice(edgeIndex, 1);

		this.remove(this.edgeLabels[edgeIndex]);
		this.edgeLabels.splice(edgeIndex, 1);

		this.remove(this.coordinateLabels[index]);
		this.coordinateLabels.splice(index, 1);

		this.remove(this.angleLabels[index]);
		this.angleLabels.splice(index, 1);

		this.spheres.splice(index, 1);

		this.update();

		this.dispatchEvent({ type: 'marker_removed', measurement: this });
	};

	setMarker(index, point) {
		this.points[index] = point;

		let event = {
			type: 'marker_moved',
			measure: this,
			index: index,
			position: point.position.clone()
		};
		this.dispatchEvent(event);

		this.update();
	}

	setPosition(index, position) {
		let point = this.points[index];
		point.position.copy(position);
		let event = {
			type: 'marker_moved',
			measure: this,
			index: index,
			position: position.clone()
		};
		this.dispatchEvent(event);

		this.update();
	};

	setPositionAndTemperature(index, position, temperature) {
		let point = this.points[index];
		point.position.copy(position);
		let event = {
			type: 'marker_moved',
			measure: this,
			index: index,
			position: position.clone(),
			temperature: temperature
		};
		this.dispatchEvent(event);

		this.update();
	};

	getArea() {
		let area = 0;
		let j = this.points.length - 1;

		for (let i = 0; i < this.points.length; i++) {
			let p1 = this.points[i].position;
			let p2 = this.points[j].position;
			area += (p2.x + p1.x) * (p1.y - p2.y);
			j = i;
		}

		return Math.abs(area / 2);
	};

	getTotalDistance() {
		if (this.points.length === 0) {
			return 0;
		}

		let distance = 0;

		for (let i = 1; i < this.points.length; i++) {
			let prev = this.points[i - 1].position;
			let curr = this.points[i].position;
			let d = prev.distanceTo(curr);

			distance += d;
		}

		if (this.closed && this.points.length > 1) {
			let first = this.points[0].position;
			let last = this.points[this.points.length - 1].position;
			let d = last.distanceTo(first);

			distance += d;
		}

		return distance;
	}

	getAngleBetweenLines(cornerPoint, point1, point2) {
		let v1 = new THREE.Vector3().subVectors(point1.position, cornerPoint.position);
		let v2 = new THREE.Vector3().subVectors(point2.position, cornerPoint.position);

		// avoid the error printed by threejs if denominator is 0
		const denominator = Math.sqrt(v1.lengthSq() * v2.lengthSq());
		if (denominator === 0) {
			return 0;
		} else {
			return v1.angleTo(v2);
		}
	};

	getAngle(index) {
		if (this.points.length < 3 || index >= this.points.length) {
			return 0;
		}

		let previous = (index === 0) ? this.points[this.points.length - 1] : this.points[index - 1];
		let point = this.points[index];
		let next = this.points[(index + 1) % (this.points.length)];

		return this.getAngleBetweenLines(point, previous, next);
	}

	// updateAzimuth(){
	// 	// if(this.points.length !== 2){
	// 	// 	return;
	// 	// }

	// 	// const azimuth = this.azimuth;

	// 	// const [p0, p1] = this.points;

	// 	// const r = p0.position.distanceTo(p1.position);

	// }

	updateDepth() {
		// 读取 this._depthMode。此前两处硬伤导致 depthMode 切换实际同步不到材质：
		// ① 误读全文件未定义的 this._showDepth（falsy，一切换就把深度测试关掉）；
		// ② areaLabel/circle/azimuth 各赋值漏了点号（只是往 this 挂垃圾属性）
		this.spheres.forEach(sphere => {
			sphere.material.depthTest = this._depthMode;
			sphere.material.depthWrite = this._depthMode;
		});
		this.edges.forEach(edge => {
			edge.material.depthTest = this._depthMode;
		});
		this.edgeLabels.forEach(label => {
			label.material.depthTest = this._depthMode;
		});
		this.angleLabels.forEach(label => {
			label.material.depthTest = this._depthMode;
		});
		this.coordinateLabels.forEach(label => {
			label.material.depthTest = this._depthMode;
		});

		if (this.heightEdge.material) {
			this.heightEdge.material.depthTest = this._depthMode;
		}
		if (this.heightLabel.material) {
			this.heightLabel.material.depthTest = this._depthMode;
		}
		if (this.areaLabel.material) {
			this.areaLabel.material.depthTest = this._depthMode;
		}
		if (this.circleRadiusLabel.material) {
			this.circleRadiusLabel.material.depthTest = this._depthMode;
		}
		if (this.circleRadiusLine.material) {
			this.circleRadiusLine.material.depthTest = this._depthMode;
		}
		if (this.circleLine.material) {
			this.circleLine.material.depthTest = this._depthMode;
		}
		if (this.circleCenter.material) {
			this.circleCenter.material.depthTest = this._depthMode;
		}
		if (this.azimuth.label) {
			this.azimuth.label.material.depthTest = this._depthMode;
			this.azimuth.centerToNorth.material.depthTest = this._depthMode;
			this.azimuth.centerToTarget.material.depthTest = this._depthMode;
			this.azimuth.centerToTargetground.material.depthTest = this._depthMode;
			this.azimuth.targetgroundToTarget.material.depthTest = this._depthMode;
		}
	}

	update() {
		if (this.points.length === 0) {
			return;
		} else if (this.points.length === 1 && this._isTemperature === true) {
			let point = this.points[0];
			let position = point.position;
			this.spheres[0].position.copy(position);

			{ // coordinate labels
				let coordinateLabel = this.coordinateLabels[0];
				let msg = position.toArray().map(p => Utils.addCommas(p.toFixed(2))).join(" / ") + " | " + point.temperature + " ℃";
				coordinateLabel.setText(msg);

				coordinateLabel.visible = this.showCoordinates;
			}

			return;
		} else if (this.points.length === 1) {
			let point = this.points[0];
			let position = point.position;
			this.spheres[0].position.copy(position);

			{ // coordinate labels
				let coordinateLabel = this.coordinateLabels[0];
				let msg = position.toArray().map(p => Utils.addCommas(p.toFixed(2))).join(" / ");
				coordinateLabel.setText(msg);

				coordinateLabel.visible = this.showCoordinates;
			}

			return;
		}

		let lastIndex = this.points.length - 1;

		let centroid = new THREE.Vector3();
		for (let i = 0; i <= lastIndex; i++) {
			let point = this.points[i];
			centroid.add(point.position);
		}
		centroid.divideScalar(this.points.length);

		for (let i = 0; i <= lastIndex; i++) {
			let index = i;
			let nextIndex = (i + 1 > lastIndex) ? 0 : i + 1;
			let previousIndex = (i === 0) ? lastIndex : i - 1;

			let point = this.points[index];
			let nextPoint = this.points[nextIndex];
			let previousPoint = this.points[previousIndex];

			let sphere = this.spheres[index];

			// spheres
			sphere.position.copy(point.position);
			sphere.material.color = this.color;

			{ // edges
				let edge = this.edges[index];

				edge.material.color = this.color;

				edge.position.copy(point.position);

				edge.geometry.setPositions([
					0, 0, 0,
					...nextPoint.position.clone().sub(point.position).toArray(),
				]);

				// 重构178 核心语法错误修复 verticesNeedUpdate
				// edge.geometry.verticesNeedUpdate = true;
				edge.geometry.computeBoundingSphere();
				edge.computeLineDistances();
				edge.visible = index < lastIndex || this.closed;

				if (!this.showEdges) {
					edge.visible = false;
				}
			}

			{ // edge labels
				let edgeLabel = this.edgeLabels[i];

				let center = new THREE.Vector3().add(point.position);
				center.add(nextPoint.position);
				center = center.multiplyScalar(0.5);
				let distance = point.position.distanceTo(nextPoint.position);

				edgeLabel.position.copy(center);

				let suffix = "";
				if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
					distance = distance / this.lengthUnit.unitspermeter * this.lengthUnitDisplay.unitspermeter;  //convert to meters then to the display unit
					suffix = this.lengthUnitDisplay.code;
				}

				let txtLength = Utils.addCommas(distance.toFixed(2));
				edgeLabel.setText(`${txtLength} ${suffix}`);
				edgeLabel.visible = this.showDistances && (index < lastIndex || this.closed) && this.points.length >= 2 && distance > 0;
			}

			{ // angle labels
				let angleLabel = this.angleLabels[i];
				let angle = this.getAngleBetweenLines(point, previousPoint, nextPoint);

				let dir = nextPoint.position.clone().sub(previousPoint.position);
				dir.multiplyScalar(0.5);
				dir = previousPoint.position.clone().add(dir).sub(point.position).normalize();

				let dist = Math.min(point.position.distanceTo(previousPoint.position), point.position.distanceTo(nextPoint.position));
				dist = dist / 9;

				let labelPos = point.position.clone().add(dir.multiplyScalar(dist));
				angleLabel.position.copy(labelPos);

				let msg = Utils.addCommas((angle * (180.0 / Math.PI)).toFixed(1)) + '\u00B0';
				angleLabel.setText(msg);

				angleLabel.visible = this.showAngles && (index < lastIndex || this.closed) && this.points.length >= 3 && angle > 0;
			}
		}

		{ // update height stuff
			let heightEdge = this.heightEdge;
			heightEdge.visible = this.showHeight;
			this.heightLabel.visible = this.showHeight;

			if (this.showHeight) {
				let sorted = this.points.slice().sort((a, b) => a.position.z - b.position.z);
				let lowPoint = sorted[0].position.clone();
				let highPoint = sorted[sorted.length - 1].position.clone();
				let min = lowPoint.z;
				let max = highPoint.z;
				let height = max - min;

				let start = new THREE.Vector3(highPoint.x, highPoint.y, min);
				let end = new THREE.Vector3(highPoint.x, highPoint.y, max);

				heightEdge.position.copy(lowPoint);

				heightEdge.geometry.setPositions([
					0, 0, 0,
					...start.clone().sub(lowPoint).toArray(),
					...start.clone().sub(lowPoint).toArray(),
					...end.clone().sub(lowPoint).toArray(),
				]);

				// 重构178 核心语法错误修复 verticesNeedUpdate
				// heightEdge.geometry.verticesNeedUpdate = true;
				// heightEdge.geometry.computeLineDistances();
				// heightEdge.geometry.lineDistancesNeedUpdate = true;
				heightEdge.geometry.computeBoundingSphere();
				heightEdge.computeLineDistances();

				// heightEdge.material.dashSize = height / 40;
				// heightEdge.material.gapSize = height / 40;

				let heightLabelPosition = start.clone().add(end).multiplyScalar(0.5);
				this.heightLabel.position.copy(heightLabelPosition);

				let suffix = "";
				if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
					height = height / this.lengthUnit.unitspermeter * this.lengthUnitDisplay.unitspermeter;  //convert to meters then to the display unit
					suffix = this.lengthUnitDisplay.code;
				}

				let txtHeight = Utils.addCommas(height.toFixed(2));
				let msg = `${txtHeight} ${suffix}`;
				this.heightLabel.setText(msg);
			}
		}

		{ // update circle stuff
			const circleRadiusLabel = this.circleRadiusLabel;
			const circleRadiusLine = this.circleRadiusLine;
			const circleLine = this.circleLine;
			const circleCenter = this.circleCenter;

			const circleOkay = this.points.length === 3;

			circleRadiusLabel.visible = this.showCircle && circleOkay;
			circleRadiusLine.visible = this.showCircle && circleOkay;
			circleLine.visible = this.showCircle && circleOkay;
			circleCenter.visible = this.showCircle && circleOkay;

			if (this.showCircle && circleOkay) {

				const A = this.points[0].position;
				const B = this.points[1].position;
				const C = this.points[2].position;
				const AB = B.clone().sub(A);
				const AC = C.clone().sub(A);
				const N = AC.clone().cross(AB).normalize();

				const center = Potree.Utils.computeCircleCenter(A, B, C);
				const radius = center.distanceTo(A);


				const scale = radius / 20;
				circleCenter.position.copy(center);
				circleCenter.scale.set(scale, scale, scale);

				//circleRadiusLine.geometry.vertices[0].set(0, 0, 0);
				//circleRadiusLine.geometry.vertices[1].copy(B.clone().sub(center));

				// 使用判断
				if (this.hasNaNInVectorObj(B) || this.hasNaNInVectorObj(center)) {
					// console.error("ydLog: B或center包含NaN！", { B, center });
				} else {
					// 原代码逻辑
					circleRadiusLine.geometry.setPositions([
						0, 0, 0,
						...B.clone().sub(center).toArray()
					]);
				}

				// 重构178 核心语法错误修复 verticesNeedUpdate
				// circleRadiusLine.geometry.verticesNeedUpdate = true;
				circleRadiusLine.geometry.computeBoundingSphere();
				circleRadiusLine.position.copy(center);
				circleRadiusLine.computeLineDistances();

				const target = center.clone().add(N);
				circleLine.position.copy(center);
				circleLine.scale.set(radius, radius, radius);
				circleLine.lookAt(target);

				circleRadiusLabel.visible = true;
				circleRadiusLabel.position.copy(center.clone().add(B).multiplyScalar(0.5));
				circleRadiusLabel.setText(`${radius.toFixed(3)}`);

			}
		}

		{ // update area label
			this.areaLabel.position.copy(centroid);
			this.areaLabel.visible = this.showArea && this.points.length >= 3;
			let area = this.getArea();

			let suffix = "";
			if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
				area = area / Math.pow(this.lengthUnit.unitspermeter, 2) * Math.pow(this.lengthUnitDisplay.unitspermeter, 2);  //convert to square meters then to the square display unit
				suffix = this.lengthUnitDisplay.code;
			}

			let txtArea = Utils.addCommas(area.toFixed(1));
			let msg = `${txtArea} ${suffix}\u00B2`;
			this.areaLabel.setText(msg);
		}

		// this.updateAzimuth();
	};

	/**
	 * 检查对象的x/y/z属性是否包含NaN
	 * @param {Object} obj - 待检查对象（包含x/y/z属性）
	 * @returns {boolean} - true表示包含NaN/对象不存在，false表示有效
	 */
	hasNaNInVectorObj(obj) {
		if (!obj) return true; // 对象不存在视为无效
		const vectorProps = ['x', 'y', 'z']; // 2D向量可只保留x、y
		return vectorProps.some(prop => isNaN(obj[prop]));
	}

	raycast(raycaster, intersects) {
		for (let i = 0; i < this.points.length; i++) {
			let sphere = this.spheres[i];

			sphere.raycast(raycaster, intersects);
		}

		// recalculate distances because they are not necessarely correct
		// for scaled objects.
		// see https://github.com/mrdoob/three.js/issues/5827
		// TODO: remove this once the bug has been fixed
		for (let i = 0; i < intersects.length; i++) {
			let I = intersects[i];
			I.distance = raycaster.ray.origin.distanceTo(I.point);
		}
		intersects.sort(function (a, b) { return a.distance - b.distance; });
	};

	get showCoordinates() {
		return this._showCoordinates;
	}

	set showCoordinates(value) {
		this._showCoordinates = value;
		this.update();
	}

	get showAngles() {
		return this._showAngles;
	}

	set showAngles(value) {
		this._showAngles = value;
		this.update();
	}

	get showCircle() {
		return this._showCircle;
	}

	set showCircle(value) {
		this._showCircle = value;
		this.update();
	}

	get showAzimuth() {
		return this._showAzimuth;
	}

	set showAzimuth(value) {
		this._showAzimuth = value;
		this.update();
	}

	get showEdges() {
		return this._showEdges;
	}

	set showEdges(value) {
		this._showEdges = value;
		this.update();
	}

	get showHeight() {
		return this._showHeight;
	}

	set showHeight(value) {
		this._showHeight = value;
		this.update();
	}

	get showArea() {
		return this._showArea;
	}

	set showArea(value) {
		this._showArea = value;
		this.update();
	}

	get closed() {
		return this._closed;
	}

	set closed(value) {
		this._closed = value;
		this.update();
	}

	get showDistances() {
		return this._showDistances;
	}

	set showDistances(value) {
		this._showDistances = value;
		this.update();
	}
	set isTemperature(value) {
		this._isTemperature = value;
		this.update();
	}

	set depthMode(value) {
		this._depthMode = value;

		this.updateDepth();

		this.dispatchEvent({
			type: 'measure_depth_change',
			measure: this
		});
	}

	get depthMode() {
		return this._depthMode
	}
}

export class YDMeasure extends Measure {
	constructor() {
		super();
		// 显示名字只在 _showCoordinates 是true的情况下有效。_showName是true会优先显示name
		this._showID = true;
		this._showCoordinates = true;

		this._disabledPointMove = false

		Object.defineProperty(this, '_showDistances', {
			value: this._showDistances, // 继承父类的初始值
			writable: true,
			enumerable: false
		});
		// 对其他属性执行同样操作
		Object.defineProperty(this, '_showCoordinates', { value: this._showCoordinates, writable: true, enumerable: false });
		Object.defineProperty(this, '_showArea', { value: this._showArea, writable: true, enumerable: false });
		// Object.defineProperty(this, '_closed', { value: this._closed, writable: true, enumerable: false });
		Object.defineProperty(this, '_showAngles', { value: this._showAngles, writable: true, enumerable: false });
		Object.defineProperty(this, '_showCircle', { value: this._showCircle, writable: true, enumerable: false });
		Object.defineProperty(this, '_showHeight', { value: this._showHeight, writable: true, enumerable: false });
		Object.defineProperty(this, '_showEdges', { value: this._showEdges, writable: true, enumerable: false });
		Object.defineProperty(this, '_showAzimuth', { value: this._showAzimuth, writable: true, enumerable: false });
	}

	addYDMarker(point, route, line) {
		this.addMarker(point)
		let sphere = this.spheres[this.spheres.length - 1];
		sphere.userData = route;
		sphere.name = route.name || route.id;

		if (this.edgeLabels.length > 0 && line) {
			let edgeLabel = this.edgeLabels[this.edgeLabels.length - 1];
			edgeLabel.userData = line;
			edgeLabel.setText(line.name || line.id);
		}
	}

	indexOfSphereByRouteId(id) {
		return this.spheres.findIndex(sphere => sphere.userData && sphere.userData.id == id);
	}

	removeYDMarker(index) {
		this.removeMarker(index);
	}

	markerAddEventListener(sphere) {
		if (!sphere) return;
		if (!this.disabledPointMove) {
			if (this._boundMarkerDrag) {
				sphere.addEventListener('drag', this._boundMarkerDrag);
			}
			if (this._boundMarkerDrop) {
				sphere.addEventListener('drop', this._boundMarkerDrop);
			}
			if (this._boundMouseover) {
				sphere.addEventListener('mouseover', this._boundMouseover);
			}
			if (this._boundMouseleave) {
				sphere.addEventListener('mouseleave', this._boundMouseleave);
			}
		} else {
			// 如果不添加drag 单独订阅的drop可能也不会执行
			sphere.addEventListener('drag', this.emptyFun);
			if (this._boundMarkerDrop) {
				sphere.addEventListener('drop', this._boundMarkerDrop);
			}
			if (this._boundMouseover) {
				sphere.addEventListener('mouseover', this._boundMouseover);
			}
			if (this._boundMouseleave) {
				sphere.addEventListener('mouseleave', this._boundMouseleave);
			}
		}
	}


	markerRemoveEventListener(sphere) {
		if (!sphere) return;
		sphere.addEventListener('drag', this.emptyFun);
		if (this._boundMarkerDrag) {
			sphere.removeEventListener('drag', this._boundMarkerDrag);
		}
		if (this._boundMarkerDrop) {
			sphere.removeEventListener('drop', this._boundMarkerDrop);
		}
		if (this._boundMouseover) {
			sphere.removeEventListener('mouseover', this._boundMouseover);
		}
		if (this._boundMouseleave) {
			sphere.removeEventListener('mouseleave', this._boundMouseleave);
		}
	}

	// 空函数
	emptyFun() { }

	markerDrop(e) {
		let i = this.spheres.indexOf(e.drag.object);
		if (i !== -1) {
			this.dispatchEvent({
				'type': 'point_click',
				'measurement': this,
				'sphere': this.spheres[i],
				'edge': this.edgeLabels[i],
				'index': i
			});
		}
	}

	update() {
		if (this.points.length === 0) {
			return;
		} else if (this.points.length === 1) {
			let point = this.points[0];
			let position = point.position;
			this.spheres[0].position.copy(position);

			{
				// coordinate labels
				let coordinateLabel = this.coordinateLabels[0];
				let sphere = this.spheres[0];
				coordinateLabel.setText(sphere.name);

				coordinateLabel.visible = this.showCoordinates;
			}
			return;
		}

		let lastIndex = this.points.length - 1;

		let centroid = new THREE.Vector3();
		for (let i = 0; i <= lastIndex; i++) {
			let point = this.points[i];
			centroid.add(point.position);
		}
		centroid.divideScalar(this.points.length);

		for (let i = 0; i <= lastIndex; i++) {
			let index = i;
			let nextIndex = (i + 1 > lastIndex) ? 0 : i + 1;

			let point = this.points[index];
			let nextPoint = this.points[nextIndex];

			let sphere = this.spheres[index];

			let coordinateLabel = this.coordinateLabels[index];
			coordinateLabel.setText(sphere.name);
			coordinateLabel.visible = this.showCoordinates;

			// spheres
			sphere.position.copy(point.position);
			sphere.material.color = this.color;

			{ // edges
				let edge = this.edges[index];

				edge.material.color = this.color;

				edge.position.copy(point.position);

				edge.geometry.setPositions([
					0, 0, 0,
					...nextPoint.position.clone().sub(point.position).toArray(),
				]);

				// 重构178 核心语法错误修复 verticesNeedUpdate
				// edge.geometry.verticesNeedUpdate = true;
				edge.geometry.computeBoundingSphere();
				edge.computeLineDistances();
				edge.visible = index < lastIndex || this.closed;
			}

			{ // edge labels
				let edgeLabel = this.edgeLabels[i];
				let center = new THREE.Vector3().add(point.position);
				center.add(nextPoint.position);
				center = center.multiplyScalar(0.5);

				edgeLabel.position.copy(center);

				edgeLabel.visible = this.showID && index < lastIndex && this.points.length >= 2
			}
		}
	}

	get showID() {
		return this._showID;
	}

	set showID(value) {
		this._showID = value;
		this.update();
	}

	get disabledPointMove() {
		return this._disabledPointMove;
	}
	set disabledPointMove(value) {
		this._disabledPointMove = value;

		this.spheres.forEach(sphere => {
			this.markerRemoveEventListener(sphere);

			this.markerAddEventListener(sphere);
		});
	}
}
